import { Box, Button, TextField, Typography } from '@mui/material';
import { ModifierSelection } from 'components/MenuModifiers/MenuForm/ModifierSelection/ModifierSelection';
import { Option } from 'types/modifier-options.type';
import { StoreMultiSelect } from 'components/MenuModifiers/MenuForm/StoreSelection/StoreSelection';
import { allAvailableModifiers } from 'redux/modifiers/modifiers.selectors';
import { allStores } from 'redux/stores/stores.selectors';
import { menuModifierOption } from 'initial-state/menu-modifier-option';
import { useSelector } from 'react-redux';
import React, { FC, useEffect, useState } from 'react';

interface Props {
  modOption: Option;
  updateSelectedOption: (modifier: Option, originalOption?: string) => void;
  updateOption: (productId: string, updatedValues: Partial<Option>) => void;
  deleteModifierOption: (arg: string) => void;
  isSuperAdmin: boolean;
}

export const ModifierOption: FC<Props> = ({
  modOption,
  updateOption,
  deleteModifierOption,
  updateSelectedOption,
  isSuperAdmin,
}) => {
  const availableModifierOptions = useSelector(allAvailableModifiers);
  const allEdibleStores = useSelector(allStores).map((s) => s.storeNumber);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [modifierSelected, setModifierSelected] = useState<boolean>(false);
  const [modifierOption, setModifierOption] =
    useState<Option>(menuModifierOption);

  useEffect(() => {
    setModifierOption(modOption);
    setModifierSelected(true);
  }, []);

  const updateModifierOption = (updatedValues: Partial<Option>) => {
    setModifierOption((prevForm) => ({
      ...prevForm,
      ...updatedValues,
    }));

    updateOption(modOption.merchant_supplied_id, updatedValues);
  };

  useEffect(() => {
    if (selected.length > 0 || modifierOption.active_stores?.length > 0) {
      updateModifierOption({ active_stores: selected });
    }
  }, [selected]);

  const selectedModifierHandler = (productId: number) => {
    const modifier = availableModifierOptions.find(
      (p) => p.productId === productId,
    );

    if (modifier) {
      const modifierToSet = {
        merchant_supplied_id: String(modifier.productId),
        name: modifier.addonOptionName,
        active: true,
        price: Number(modifier.salePrice),
        base_price: Number(modifier.salePrice),
        original_image_url: modifier.imageUrl,
        active_stores: modifier.activeStores,
      };
      updateSelectedOption(modifierToSet, modOption.merchant_supplied_id);
      setModifierOption(modifierToSet);
      setModifierSelected(true);
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    enabled?: boolean,
  ) => {
    const { name, value } = event.target;

    if (enabled !== undefined) {
      updateModifierOption({ [name]: enabled });
    } else {
      updateModifierOption({ [name]: value });
    }
  };

  return (
    <Box margin="50px auto">
      <Box display="flex" justifyContent="center">
        <img
          src={modifierOption.original_image_url}
          alt={modifierOption.name}
          height="100px"
          width="100px"
          style={{ borderRadius: '50px' }}
        />
      </Box>
      <Box display="flex" justifyContent="end">
        <Typography>Enabled</Typography>
        <input
          name="active"
          type="checkbox"
          checked={modifierOption.active}
          disabled={!isSuperAdmin}
          onChange={(e) => handleInputChange(e, !modifierOption.active)}
        />
      </Box>
      <Box display="flex" justifyContent="end" mt={4}>
        <Button
          onClick={() =>
            deleteModifierOption(modifierOption.merchant_supplied_id)
          }
          variant="contained"
          sx={{ borderRadius: 1 }}
          disabled={!isSuperAdmin}
        >
          Delete
        </Button>
      </Box>
      <Box display="flex">
        <ModifierSelection
          isSuperAdmin={isSuperAdmin}
          availableModifierOptions={availableModifierOptions}
          onSelection={selectedModifierHandler}
          selectedModifierOption={modifierOption}
        />
        <StoreMultiSelect
          allStores={allEdibleStores}
          selectedStores={modifierOption.active_stores || []}
          value={selected}
          onChange={setSelected}
          width={400}
        />
      </Box>
      <Box mt={3} display={modifierSelected ? 'block' : 'none'}>
        <Box display="flex" justifyContent="space-around">
          <TextField
            onChange={handleInputChange}
            label="Name"
            name="name"
            variant="standard"
            required
            sx={{ width: '300px' }}
            value={modifierOption.name}
            disabled={!isSuperAdmin}
          />
          <TextField
            onChange={handleInputChange}
            label="SKU"
            name="merchant_supplied_id"
            variant="standard"
            disabled
            required
            sx={{ width: '300px' }}
            value={modifierOption.merchant_supplied_id}
          />
        </Box>
        <Box mt={3} display="flex" justifyContent="space-around">
          <TextField
            onChange={handleInputChange}
            label="Price"
            name="price"
            variant="standard"
            required
            sx={{ width: '300px' }}
            value={modifierOption.price}
            disabled={!isSuperAdmin}
          />
          <TextField
            onChange={handleInputChange}
            label="Image"
            name="original_image_url"
            variant="standard"
            required
            sx={{ width: '300px' }}
            value={modifierOption.original_image_url}
            disabled={!isSuperAdmin}
          />
        </Box>
      </Box>
    </Box>
  );
};
